import { Component, ChangeDetectionStrategy, inject, OnInit, OnDestroy, effect, output, input, computed } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputNumber } from 'primeng/inputnumber';
import { RadioButton } from 'primeng/radiobutton';
import { Select } from 'primeng/select';
import { DatePicker } from 'primeng/datepicker';
import { Card } from 'primeng/card';
import { Button } from 'primeng/button';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { PropertyWizardStateService, WizardValidationService } from '../../services';
import { SeasonPricingComponent } from './season-pricing/season-pricing.component';
import { WizardStepHintComponent } from '../wizard-step-hint/wizard-step-hint.component';
import {
  PricingRules,
  ELECTRICITY_POLICY_OPTIONS,
  PET_POLICY_OPTIONS,
  ElectricityPolicy,
  PetPolicy,
  timeHhMmValidator
} from '../../models';

@Component({
  selector: 'app-step-pricing',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    InputNumber,
    RadioButton,
    Select,
    DatePicker,
    Card,
    Button,
    TranslocoModule,
    SeasonPricingComponent,
    WizardStepHintComponent
  ],
  templateUrl: './step-pricing.component.html',
  styleUrl: './step-pricing.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StepPricingComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly stateService = inject(PropertyWizardStateService);
  private readonly validationService = inject(WizardValidationService);
  private readonly translocoService = inject(TranslocoService);

  readonly previous = output<void>();
  readonly submit = output<void>();

  readonly isViewMode = this.stateService.isViewMode;
  readonly canSubmit = this.stateService.canSubmit;
  readonly isSubmitting = input(false);

  readonly electricityOptions = computed(() =>
    ELECTRICITY_POLICY_OPTIONS.map(opt => ({
      ...opt,
      label: this.translocoService.translate(opt.labelKey)
    }))
  );

  readonly petSelectOptions = computed(() => [
    { label: this.translocoService.translate('objects.wizard.pricing.petOptions.notAllowed'), value: 'not_allowed' },
    { label: this.translocoService.translate('objects.wizard.pricing.petOptions.allowedPaidPerDay'), value: 'allowed_paid_per_day' },
    { label: this.translocoService.translate('objects.wizard.pricing.petOptions.allowedPaidPerStay'), value: 'allowed_paid_per_stay' },
    { label: this.translocoService.translate('objects.wizard.pricing.petOptions.allowedFree'), value: 'allowed_free' }
  ]);

  form!: FormGroup;

  private readonly stateEffect = effect(() => {
    const rules = this.stateService.pricingRules();
    if (rules && this.form) {
      this.patchFormFromState(rules);
    }
  });

  ngOnInit(): void {
    this.initForm();
    this.setupFormSubscription();

    const existingData = this.stateService.pricingRules();
    if (existingData) {
      this.patchFormFromState(existingData);
    }
  }

  ngOnDestroy(): void {
    this.saveToState();
  }

  private initForm(): void {
    this.form = this.fb.group({
      electricityPolicy: ['included', Validators.required],
      petPolicy: ['not_allowed', Validators.required],
      petFee: [null],
      depositPercentage: [30, [Validators.required, Validators.min(0), Validators.max(100)]],
      depositPaymentDays: [3, [Validators.min(1), Validators.max(30)]],
      cleaningFee: [null],
      checkInTime: ['15:00', [Validators.required, timeHhMmValidator()]],
      checkOutTime: ['10:00', [Validators.required, timeHhMmValidator()]],
      minStay: [1],
      maxStay: [null]
    });
  }

  private setupFormSubscription(): void {
    this.form.valueChanges.subscribe(() => {
      this.updateValidity();
    });

    this.form.get('petPolicy')?.valueChanges.subscribe((value: PetPolicy) => {
      const petFeeControl = this.form.get('petFee');
      if (value === 'allowed_paid_per_day' || value === 'allowed_paid_per_stay') {
        petFeeControl?.setValidators([Validators.required, Validators.min(1)]);
      } else {
        petFeeControl?.clearValidators();
        petFeeControl?.setValue(null);
      }
      petFeeControl?.updateValueAndValidity();
    });
  }

  private updateValidity(): void {
    const data = this.getFormData();
    const result = this.validationService.validatePricingRules(data);
    const timesValid = (this.form.get('checkInTime')?.valid ?? false) && (this.form.get('checkOutTime')?.valid ?? false);
    this.stateService.setStepValidity(2, result.isValid && timesValid);
  }

  private patchFormFromState(rules: PricingRules): void {
    this.form.patchValue({
      electricityPolicy: rules.electricityPolicy,
      petPolicy: rules.petPolicy,
      petFee: rules.petFee,
      depositPercentage: rules.depositPercentage,
      depositPaymentDays: rules.depositPaymentDays,
      cleaningFee: rules.cleaningFee,
      checkInTime: rules.checkInTime,
      checkOutTime: rules.checkOutTime,
      minStay: rules.minStay,
      maxStay: rules.maxStay
    }, { emitEvent: false });
    this.updateValidity();
  }

  private saveToState(): void {
    const data = this.getFormData();
    if (data) {
      this.stateService.updatePricingRules(data);
    }
  }

  private getFormData(): PricingRules {
    const value = this.form.getRawValue();
    return {
      electricityPolicy: value.electricityPolicy as ElectricityPolicy,
      petPolicy: value.petPolicy as PetPolicy,
      petFee: value.petFee,
      depositPercentage: value.depositPercentage,
      depositPaymentDays: value.depositPaymentDays,
      cleaningFee: value.cleaningFee,
      checkInTime: value.checkInTime || '15:00',
      checkOutTime: value.checkOutTime || '10:00',
      minStay: value.minStay || 1,
      maxStay: value.maxStay
    };
  }

  get showPetFee(): boolean {
    const policy = this.form.get('petPolicy')?.value;
    return policy === 'allowed_paid_per_day' || policy === 'allowed_paid_per_stay';
  }

  get petFeeLabel(): string {
    const policy = this.form.get('petPolicy')?.value;
    if (policy === 'allowed_paid_per_stay') {
      return this.translocoService.translate('objects.wizard.pricing.petFeePerStay');
    }
    return this.translocoService.translate('objects.wizard.pricing.petFeePerDay');
  }

  getErrorMessage(controlName: string): string | null {
    const control = this.form.get(controlName);
    if (!control || !control.errors || !control.touched) return null;

    const errors = control.errors;
    if (errors['required']) return this.translocoService.translate('objects.wizard.validation.required');
    if (errors['min']) return this.translocoService.translate('objects.wizard.validation.minValue', { min: errors['min'].min });
    if (errors['max']) return this.translocoService.translate('objects.wizard.validation.maxValue', { max: errors['max'].max });
    if (errors['timeFormat']) return this.translocoService.translate('objects.wizard.validation.timeFormat');

    return this.translocoService.translate('objects.wizard.validation.invalidValue');
  }

  onPrevious(): void {
    this.saveToState();
    this.stateService.previousStep();
    this.previous.emit();
  }

  onSubmit(): void {
    this.saveToState();
    this.submit.emit();
  }
}
